import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Clock, FileText, Loader2, ArrowRight } from 'lucide-react';
import { formatCurrency, formatDateShort, getInvoiceStatusColor } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Validation() {
  const navigate = useNavigate();
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending');

  useEffect(() => {
    const fetch = async () => {
      try {
        const [pRes, hRes] = await Promise.all([
          axios.get(`${API_URL}/validation/pending`),
          axios.get(`${API_URL}/validation/history`).catch(() => ({ data: [] }))
        ]);
        setPending(pRes.data);
        setHistory(hRes.data);
      } catch (e) { toast.error('Erreur chargement'); }
      finally { setLoading(false); }
    };
    fetch();
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <div><h1 className="text-2xl font-bold text-slate-900 dark:text-white">Mes validations</h1><p className="text-slate-500">{pending.length} facture(s) en attente</p></div>

      <div className="flex gap-2 mb-4">
        <Button variant={tab === 'pending' ? 'default' : 'outline'} onClick={() => setTab('pending')} className={tab === 'pending' ? 'bg-[#0F5156]' : ''}>
          <Clock className="w-4 h-4 mr-2" /> En attente ({pending.length})
        </Button>
        <Button variant={tab === 'history' ? 'default' : 'outline'} onClick={() => setTab('history')} className={tab === 'history' ? 'bg-[#0F5156]' : ''}>
          <CheckCircle2 className="w-4 h-4 mr-2" /> Historique
        </Button>
      </div>

      {tab === 'pending' && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pending.map(inv => (
            <Card key={inv.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/invoices/${inv.id}`)}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between mb-3">
                  <div><p className="font-semibold">{inv.supplier_name || 'Fournisseur'}</p><p className="text-sm text-slate-500">{inv.internal_ref}</p></div>
                  <Badge className="bg-blue-100 text-blue-700">{inv.current_step_name}</Badge>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-500">N° Facture</span><span>{inv.invoice_number}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Montant TTC</span><span className="font-semibold text-[#0F5156]">{formatCurrency(inv.montant_ttc)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Date facture</span><span>{formatDateShort(inv.date_facture)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-500">Échéance</span><span>{formatDateShort(inv.date_echeance)}</span></div>
                </div>
                <Button variant="outline" size="sm" className="w-full mt-4"><ArrowRight className="w-4 h-4 mr-2" /> Valider</Button>
              </CardContent>
            </Card>
          ))}
          {pending.length === 0 && <div className="col-span-3 text-center py-12 text-slate-500"><CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-green-400" /><p>Aucune facture en attente de validation</p></div>}
        </div>
      )}

      {tab === 'history' && (
        <Card><CardContent className="pt-6">
          <div className="space-y-4">
            {history.map((action, i) => (
              <div key={i} className="flex items-center gap-4 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div className={`w-3 h-3 rounded-full ${action.action === 'approved' ? 'bg-green-500' : action.action === 'rejected' ? 'bg-red-500' : 'bg-orange-500'}`} />
                <div className="flex-1">
                  <p className="font-medium">{action.step_name} - {action.action === 'approved' ? 'Approuvée' : action.action === 'rejected' ? 'Rejetée' : 'Modification demandée'}</p>
                  {action.comment && <p className="text-sm text-slate-500 italic">"{action.comment}"</p>}
                </div>
                <span className="text-sm text-slate-400">{formatDateShort(action.timestamp)}</span>
              </div>
            ))}
            {history.length === 0 && <p className="text-center text-slate-500">Aucune action</p>}
          </div>
        </CardContent></Card>
      )}
    </div>
  );
}
